import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Type, Briefcase, Phone, Globe, Plus, Trash2, AlertTriangle, X, Mail, Tag, Pencil, CheckCircle, User, MessageSquare, Send } from "lucide-react";
import { updateUiStrings, getUiStrings } from "../../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { EMPTY_FORM_SERVICES, EMPTY_FORM_METHODS, EMPTY_FORM_PREFIXES, EMPTY_FORM_NATIONALITIES, EMPTY_SEARCH_RESULT, TOAST_SAVE_ERROR, TOAST_REMOVE_ERROR, TOAST_SAVED, PLH_SERVICE_NAME, PLH_METHOD_NAME, PLH_NATIONALITY, PLH_PHONE_PREFIX, PLH_COUNTRY, PLH_FLAG_EMOJI, PLH_SEARCH, TIP_EDIT, TIP_DELETE, PLH_NAME } from "../../lib/constants";
import { FORM_TABS } from "../../lib/tabs";

function UiField({ label, value, onChange, multiline = false, preview }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">{label}</span>
        {preview && <span className="text-[8px] text-fendi/40 italic truncate ml-2">{preview(value)}</span>}
      </div>
      <Tag className="w-full text-xs px-3 py-2 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" rows={multiline ? 2 : undefined} value={value || ""} onChange={e => onChange(e.target.value)} />
    </label>
  );
}

function Modal({ open, title, icon: Icon, children, onSave, onCancel, saveLabel, saving, readOnly }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { if (!readOnly) onSave(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, readOnly]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4 w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne">{Icon && <Icon size={15} />}</div>
            <p className="text-sm font-medium text-softblack">{title}</p>
          </div>
          <button onClick={onCancel} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
        </div>
        <div className="space-y-4">{children}</div>
        {!readOnly && (
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={onCancel} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
            <button onClick={onSave} disabled={saving} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5">
              <Save size={12} /> {saving ? "A guardar..." : saveLabel || "Guardar"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ConfirmDialog({ open, onConfirm, onCancel, label }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onCancel(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4 w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-red-50 shrink-0"><AlertTriangle size={18} className="text-red-500" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-softblack">Eliminar item</p>
            <p className="text-xs text-warmgray mt-1">Tem a certeza que deseja eliminar <strong>{label}</strong>?</p>
          </div>
          <button onClick={onCancel} className="p-1 text-warmgray hover:text-softblack shrink-0"><X size={14} /></button>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onCancel} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
          <button onClick={onConfirm} className="px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:opacity-90">Eliminar</button>
        </div>
      </div>
    </div>
  );
}

function EditableList({ title, icon: Icon, items, onSave, saving, readOnly, emptyLabel, createEmpty, renderItem, fields, itemLabel, searchable, columns }) {
  const [localItems, setLocalItems] = useState(items);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [modal, setModal] = useState(null);
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({});
  const [savingItem, setSavingItem] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const PAGE_SIZES = [5, 10, 20, 50];

  useEffect(() => { setLocalItems(items); setPage(1); }, [items]);

  const filtered = search ? localItems.filter(item => {
    const str = (typeof item === "string" ? item : JSON.stringify(item)).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const q = search.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    let qi = 0;
    for (let si = 0; si < str.length && qi < q.length; si++) {
      if (str[si] === q[qi]) qi++;
    }
    return qi === q.length;
  }) : localItems;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  function openAdd() {
    setEditIdx(null);
    setForm(createEmpty());
    setModal("form");
  }

  function openEdit(idx) {
    setEditIdx(idx);
    const item = localItems[idx];
    setForm(typeof item === "string" ? item : { ...item });
    setModal("form");
  }

  function closeModal() { setModal(null); setForm({}); setEditIdx(null); }

  async function handleSaveItem() {
    setSavingItem(true);
    try {
      const savedItem = typeof form === "object" && form !== null ? { ...form } : form;
      let next;
      if (editIdx !== null) {
        next = [...localItems];
        next[editIdx] = savedItem;
      } else {
        next = [...localItems, savedItem];
      }
      await onSave(next);
      setLocalItems(next);
      closeModal();
    } catch (err) { toast.error(TOAST_SAVE_ERROR); }
    finally { setSavingItem(false); }
  }

  async function remove(idx) {
    const next = localItems.filter((_, i) => i !== idx);
    try {
      await onSave(next);
      setLocalItems(next);
      setConfirmDelete(null);
    } catch (err) { toast.error(TOAST_REMOVE_ERROR); }
  }

  async function move(idx, dir) {
    const to = idx + dir;
    if (to < 0 || to >= localItems.length) return;
    const next = [...localItems];
    [next[idx], next[to]] = [next[to], next[idx]];
    try {
      await onSave(next);
      setLocalItems(next);
    } catch (err) { toast.error(TOAST_SAVE_ERROR); }
  }

  return (
    <div data-id="admin-form-config" className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <Icon size={15} className="text-champagne shrink-0" />
          <span className="text-xs font-medium text-softblack">{title}</span>
          <span className="text-[9px] text-warmgray/60 bg-fendi/20 px-1.5 py-0.5 rounded-full">{filtered.length}</span>
        </div>
        <div className="flex items-center gap-2">
          {searchable !== false && (
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder={PLH_SEARCH} className="w-36 text-[10px] px-2 py-1.5 border border-fendi/30 rounded-md bg-white focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
          )}
          {!readOnly && (
            <button onClick={openAdd} className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-medium text-champagne hover:text-softblack transition-colors">
              <Plus size={12} /> Adicionar
            </button>
          )}
        </div>
      </div>

      <Modal open={modal === "form"} title={editIdx !== null ? `Editar ${itemLabel}` : `Novo ${itemLabel}`} icon={Icon} onSave={handleSaveItem} onCancel={closeModal} saveLabel={editIdx !== null ? "Guardar" : "Adicionar"} saving={savingItem} readOnly={readOnly}>
        {fields ? fields(form, (key, value) => key === "" ? setForm(value) : setForm(f => ({ ...f, [key]: value }))) : (
          <input className="w-full text-xs px-3 py-2 border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={PLH_NAME} />
        )}
      </Modal>

      {filtered.length === 0 ? (
        <p className="text-xs text-warmgray py-8 text-center">{search ? EMPTY_SEARCH_RESULT : emptyLabel}</p>
      ) : (
        <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fendi/20 bg-fendi/5">
                  <th className="w-8 px-5 py-3" />
                  {columns ? columns.map((col, i) => (
                    <th key={i} className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">{col}</th>
                  )) : (
                    <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">{itemLabel || "Valor"}</th>
                  )}
                  {!readOnly && <th className="w-20 px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-right">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((item, i) => {
                  const realIdx = (safePage - 1) * pageSize + i;
                  return (
                  <tr key={realIdx} className="border-b border-fendi/10 hover:bg-fendi/5 transition-colors group">
                    <td className="px-5 py-4 align-middle w-8">
                      <div className="flex flex-col gap-0.5 items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => move(realIdx, -1)} disabled={realIdx === 0} className="p-0.5 text-warmgray/30 hover:text-softblack disabled:opacity-20">
                          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 15l-6-6-6 6"/></svg>
                        </button>
                        <button onClick={() => move(realIdx, 1)} disabled={realIdx === localItems.length - 1} className="p-0.5 text-warmgray/30 hover:text-softblack disabled:opacity-20">
                          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6"/></svg>
                        </button>
                      </div>
                    </td>
                    {renderItem ? renderItem(item, realIdx) : <td className="px-5 py-4 align-middle text-sm text-softblack">{typeof item === "string" ? item : item.name || item.country || ""}</td>}
                    {!readOnly && (
                      <td className="px-5 py-4 align-middle text-right whitespace-nowrap w-20">
                        <button onClick={() => openEdit(realIdx)} className="p-1.5 text-warmgray/40 hover:text-champagne transition-colors" title={TIP_EDIT}><Pencil size={12} /></button>
                        <button onClick={() => setConfirmDelete(realIdx)} className="p-1.5 text-warmgray/30 hover:text-red-600 transition-colors" title={TIP_DELETE}><Trash2 size={12} /></button>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filtered.length > pageSize && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-fendi/10 bg-fendi/5">
          <div className="flex items-center gap-3">
            <p className="text-xs text-warmgray">{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} de {filtered.length}</p>
            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
              className="text-xs border border-fendi/30 rounded-lg px-2 py-1 bg-white focus:border-champagne outline-none">
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / pág</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
              className="w-7 h-7 flex items-center justify-center border border-fendi/30 rounded hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let n;
              if (totalPages <= 5) n = i + 1;
              else if (safePage <= 3) n = i + 1;
              else if (safePage >= totalPages - 2) n = totalPages - 4 + i;
              else n = safePage - 2 + i;
              return (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-7 h-7 flex items-center justify-center rounded text-[11px] transition-colors ${safePage === n ? "bg-softblack text-white" : "border border-fendi/30 hover:bg-fendi/20"}`}>{n}</button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
              className="w-7 h-7 flex items-center justify-center border border-fendi/30 rounded hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirmDelete !== null} onConfirm={() => { remove(confirmDelete); }} onCancel={() => setConfirmDelete(null)}
        label={confirmDelete !== null ? (typeof localItems[confirmDelete] === "string" ? localItems[confirmDelete] : localItems[confirmDelete]?.country || localItems[confirmDelete]?.name || "este item") : ""} />
    </div>
  );
}

export default function AdminFormConfig({ readOnly = false, subTabModes = {}, uiStrings, setUiStrings, saving, handleSaveUiStrings, formServices, setFormServices, handleSaveFormServices, contactMethods, setContactMethods, handleSaveContactMethods, prefixes, setPrefixes, handleSavePrefixes, nationalities, setNationalities, handleSaveNationalities, initialTab, embedded, sectionHeaders, setSectionHeaders, handleSaveSectionHeaders, parentSaving, activeLang }) {
  const [activeFormTab, setActiveFormTab] = useState(initialTab || "labels");
  useEffect(() => { if (initialTab) setActiveFormTab(initialTab); }, [initialTab]);

  function isReadOnly(sub) { return readOnly || subTabModes[`form_${sub}`] === "readonly"; }

  const set = (context, key, value) => setUiStrings({ ...uiStrings, [context]: { ...uiStrings[context], [key]: value } });

  return (
    <Tabs value={activeFormTab} onValueChange={setActiveFormTab} className="w-full">
      {!embedded && (
        <div className="overflow-x-auto no-scrollbar mb-6">
          <TabsList className="w-max min-w-full border-b border-fendi p-0 h-auto justify-start flex-nowrap rounded-none bg-transparent">
            {FORM_TABS.map(({ value, icon, label }) => (
              <TabsTrigger key={value} value={value} className="relative shrink-0 px-5 py-3 data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none text-[10px] uppercase tracking-wider gap-1.5 text-warmgray">
                <icon size={13} />{label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      )}

      <TabsContent value="labels" className="mt-0">
        <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0"><Tag size={16} /></div>
              <div>
                <h3 className="text-sm font-medium text-softblack">Labels do Formulário</h3>
                <p className="text-[10px] text-warmgray mt-0.5">Textos dos campos, placeholders e botão de envio</p>
              </div>
            </div>
            {!isReadOnly("labels") && <button onClick={() => { const d = {}; ["name_label","name_placeholder","email_label","email_placeholder","phone_label","phone_placeholder","service_label","service_placeholder","message_label","message_placeholder","contact_label","contact_placeholder","nationality_label","nationality_placeholder","submit"].forEach(k => { d[k] = uiStrings?.form?.[k] || ""; }); handleSaveUiStrings("form", d); }} disabled={parentSaving} className="btn-primary !py-2 !px-4 !text-[10px] shrink-0"><Save size={12} className="mr-1.5" /> Guardar</button>}
          </div>
          <div className="p-6 space-y-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
              <div className="space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2"><User size={12} /> Nome</p>
                <UiField label="Label" value={uiStrings?.form?.name_label || ""} onChange={v => set("form", "name_label", v)} preview={v => v || "ex: Nome"} />
                <UiField label="Placeholder" value={uiStrings?.form?.name_placeholder || ""} onChange={v => set("form", "name_placeholder", v)} preview={v => v || "ex: Seu nome"} />
              </div>
              <div className="space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2"><Mail size={12} /> Email</p>
                <UiField label="Label" value={uiStrings?.form?.email_label || ""} onChange={v => set("form", "email_label", v)} preview={v => v || "ex: Email"} />
                <UiField label="Placeholder" value={uiStrings?.form?.email_placeholder || ""} onChange={v => set("form", "email_placeholder", v)} preview={v => v || "ex: seu@email.com"} />
              </div>
              <div className="space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2"><Phone size={12} /> Telefone</p>
                <UiField label="Label" value={uiStrings?.form?.phone_label || ""} onChange={v => set("form", "phone_label", v)} preview={v => v || "ex: Telefone"} />
                <UiField label="Placeholder" value={uiStrings?.form?.phone_placeholder || ""} onChange={v => set("form", "phone_placeholder", v)} preview={v => v || "ex: +351"} />
              </div>
              <div className="space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2"><Briefcase size={12} /> Serviço</p>
                <UiField label="Label" value={uiStrings?.form?.service_label || ""} onChange={v => set("form", "service_label", v)} preview={v => v || "ex: Serviço"} />
                <UiField label="Placeholder" value={uiStrings?.form?.service_placeholder || ""} onChange={v => set("form", "service_placeholder", v)} preview={v => v || "ex: Selecione"} />
              </div>
              <div className="space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2">&#9993; Contacto</p>
                <UiField label="Label" value={uiStrings?.form?.contact_label || ""} onChange={v => set("form", "contact_label", v)} preview={v => v || "ex: Contacto Pref."} />
                <UiField label="Placeholder" value={uiStrings?.form?.contact_placeholder || ""} onChange={v => set("form", "contact_placeholder", v)} preview={v => v || "ex: Selecione"} />
              </div>
              <div className="space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2"><Globe size={12} /> Nacionalidade</p>
                <UiField label="Label" value={uiStrings?.form?.nationality_label || ""} onChange={v => set("form", "nationality_label", v)} preview={v => v || "ex: Nacionalidade"} />
                <UiField label="Placeholder" value={uiStrings?.form?.nationality_placeholder || ""} onChange={v => set("form", "nationality_placeholder", v)} preview={v => v || "ex: Selecione"} />
              </div>
            </div>
            <div className="border-t border-fendi/10 pt-6">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="space-y-4">
                  <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2"><MessageSquare size={12} /> Mensagem</p>
                  <UiField label="Label" value={uiStrings?.form?.message_label || ""} onChange={v => set("form", "message_label", v)} preview={v => v || "ex: Mensagem"} />
                  <UiField label="Placeholder" value={uiStrings?.form?.message_placeholder || ""} onChange={v => set("form", "message_placeholder", v)} preview={v => v || "ex: Escreva sua mensagem"} />
                </div>
                <div className="space-y-4">
                  <p className="text-[10px] uppercase tracking-wider text-champagne font-medium flex items-center gap-2"><Send size={12} /> Botão</p>
                  <UiField label="Texto do Botão" value={uiStrings?.form?.submit || ""} onChange={v => set("form", "submit", v)} preview={v => v || "ex: Enviar"} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="services" className="mt-0">
        <EditableList title="Serviços do Formulário" icon={Briefcase} items={formServices || []} onSave={handleSaveFormServices} saving={parentSaving} readOnly={isReadOnly("services")}
          emptyLabel={EMPTY_FORM_SERVICES} createEmpty={() => ""} itemLabel="Serviço"
          renderItem={(item) => <td className="px-5 py-4 align-middle text-sm text-softblack">{item}</td>} fields={(form, set) => (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Nome <span className="text-red-400">*</span></span>
              <input className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={form || ""} onChange={e => set("", e.target.value)} placeholder={PLH_SERVICE_NAME} />
            </div>
          )} />
      </TabsContent>

      <TabsContent value="contacts" className="mt-0">
        <EditableList title="Métodos de Contacto" icon={Mail} items={contactMethods || []} onSave={handleSaveContactMethods} saving={parentSaving} readOnly={isReadOnly("contacts")}
          emptyLabel={EMPTY_FORM_METHODS} createEmpty={() => ""} itemLabel="Contacto"
          renderItem={(item) => <td className="px-5 py-4 align-middle text-sm text-softblack">{item}</td>} fields={(form, set) => (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Nome <span className="text-red-400">*</span></span>
              <input className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={form || ""} onChange={e => set("", e.target.value)} placeholder={PLH_METHOD_NAME} />
            </div>
          )} />
      </TabsContent>

      <TabsContent value="prefixes" className="mt-0">
        <EditableList title="Prefixos Telefónicos" icon={Phone} items={prefixes || []} onSave={handleSavePrefixes} saving={parentSaving} readOnly={isReadOnly("prefixes")}
          emptyLabel={EMPTY_FORM_PREFIXES} createEmpty={() => ({ code: "", country: "", flag: "" })} itemLabel="Prefixo"
          columns={["Bandeira", "Código", "País"]}
          renderItem={(item) => (
            <>
              <td className="px-5 py-4 align-middle w-16"><span className="text-lg">{item.flag || "—"}</span></td>
              <td className="px-5 py-4 align-middle w-24"><span className="font-mono text-sm text-softblack font-medium">{item.code || "—"}</span></td>
              <td className="px-5 py-4 align-middle"><span className="text-xs text-warmgray">{item.country || "—"}</span></td>
            </>
          )}
          fields={(form, set) => (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Código</span>
                <input className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={form.code || ""} onChange={e => set("code", e.target.value)} placeholder={PLH_PHONE_PREFIX} />
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">País</span>
                <input className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={form.country || ""} onChange={e => set("country", e.target.value)} placeholder={PLH_COUNTRY} />
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Bandeira</span>
                <input className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={form.flag || ""} onChange={e => set("flag", e.target.value)} placeholder={PLH_FLAG_EMOJI} />
              </div>
            </div>
          )} />
      </TabsContent>

      <TabsContent value="nationalities" className="mt-0">
        <EditableList title="Nacionalidades" icon={Globe} items={nationalities || []} onSave={handleSaveNationalities} saving={parentSaving} readOnly={isReadOnly("nationalities")}
          emptyLabel={EMPTY_FORM_NATIONALITIES} createEmpty={() => ""} itemLabel="Nacionalidade"
          renderItem={(item) => <td className="px-5 py-4 align-middle text-sm text-softblack">{item}</td>} fields={(form, set) => (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Nome <span className="text-red-400">*</span></span>
              <input className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={form || ""} onChange={e => set("", e.target.value)} placeholder={PLH_NATIONALITY} />
            </div>
          )} />
      </TabsContent>

      <TabsContent value="texts" className="mt-0">
        <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0"><Type size={16} /></div>
              <div>
                <h3 className="text-sm font-medium text-softblack">Textos da Secção</h3>
                <p className="text-[10px] text-warmgray mt-0.5">Cabeçalho e mensagens de feedback do formulário</p>
              </div>
            </div>
            {!isReadOnly("texts") && <button onClick={async () => {
              const hd = {}; ["eyebrow","headline","description"].forEach(k => { hd[k] = uiStrings?.contact_section?.[k] || ""; });
              const fd = {}; ["success_title","success_message","error_message","received_badge","send_new"].forEach(k => { fd[k] = uiStrings?.feedback?.[k] || ""; });
              await Promise.all([handleSaveUiStrings("contact_section", hd), handleSaveUiStrings("feedback", fd)]);
            }} disabled={parentSaving} className="btn-primary !py-2 !px-4 !text-[10px] shrink-0"><Save size={12} className="mr-1.5" /> Guardar</button>}
          </div>
          <div className="p-6 space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0"><Type size={16} /></div>
                <div>
                  <p className="text-xs font-medium text-softblack">Cabeçalho</p>
                  <p className="text-[10px] text-warmgray">Títulos da secção de contacto na página principal</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <UiField label="Eyebrow" value={uiStrings?.contact_section?.eyebrow || ""} onChange={v => set("contact_section", "eyebrow", v)} preview={v => v || "ex: Contacto"} />
                <UiField label="Headline" value={uiStrings?.contact_section?.headline || ""} onChange={v => set("contact_section", "headline", v)} preview={v => v || "ex: Vamos conversar"} />
                <div className="md:col-span-2">
                  <UiField label="Descrição" value={uiStrings?.contact_section?.description || ""} onChange={v => set("contact_section", "description", v)} multiline preview={v => v ? v.substring(0, 60) + "..." : "ex: Estou aqui para ajudar"} />
                </div>
              </div>
            </div>
            <div className="border-t border-fendi/10 pt-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center text-green-600 shrink-0"><CheckCircle size={16} /></div>
                <div>
                  <p className="text-xs font-medium text-softblack">Sucesso</p>
                  <p className="text-[10px] text-warmgray">Mensagens exibidas quando o formulário é enviado com sucesso</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <UiField label="Badge" value={uiStrings?.feedback?.received_badge || ""} onChange={v => set("feedback", "received_badge", v)} preview={v => v || "ex: Recebido"} />
                <UiField label="Título" value={uiStrings?.feedback?.success_title || ""} onChange={v => set("feedback", "success_title", v)} preview={v => v || "ex: Obrigado!"} />
                <div className="md:col-span-2">
                  <UiField label="Mensagem" value={uiStrings?.feedback?.success_message || ""} onChange={v => set("feedback", "success_message", v)} multiline preview={v => v ? v.substring(0, 60) + "..." : "ex: Recebemos a sua mensagem"} />
                </div>
                <UiField label="Botão: Enviar Novo" value={uiStrings?.feedback?.send_new || ""} onChange={v => set("feedback", "send_new", v)} preview={v => v || "ex: Enviar outra"} />
              </div>
            </div>
            <div className="border-t border-fendi/10 pt-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-red-500 shrink-0"><X size={16} /></div>
                <div>
                  <p className="text-xs font-medium text-softblack">Erro</p>
                  <p className="text-[10px] text-warmgray">Mensagem exibida quando ocorre uma falha no envio</p>
                </div>
              </div>
              <UiField label="Mensagem de Erro" value={uiStrings?.feedback?.error_message || ""} onChange={v => set("feedback", "error_message", v)} multiline preview={v => v ? v.substring(0, 60) + "..." : "ex: Erro ao enviar"} />
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
